import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Flame, Users, MapPin, Truck, Store, Info } from "lucide-react";
import { addDays, startOfDay, parseISO, getDay } from "date-fns";
import { TestimonialStrip } from "@/components/social/TestimonialStrip";

/** Generate 30-minute time slots from 8:00 AM to 10:00 PM */
function buildTimeSlots(): { value: string; label: string }[] {
  const slots: { value: string; label: string }[] = [];
  for (let h = 8; h <= 22; h++) {
    for (const m of [0, 30]) {
      if (h === 22 && m === 30) break;
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      const value = `${hh}:${mm}`;
      const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
      const ampm = h >= 12 ? "PM" : "AM";
      const label = `${hour12}:${mm} ${ampm}`;
      slots.push({ value, label });
    }
  }
  return slots;
}
const TIME_SLOTS = buildTimeSlots();

/** Format digits as (XXX) XXX-XXXX as the user types */
function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

import type { CartItem } from "./OrderCart";
import type { OrderSettings } from "@/hooks/useOrderSettings";
import { parseDeliveryFee } from "@/lib/delivery-fee";

interface OrderCheckoutProps {
  items: CartItem[];
  subtotal: number;
  onBack: () => void;
  onSubmit: (data: CheckoutData) => void;
  isSubmitting: boolean;
  settings: OrderSettings;
  upfrontFulfillment?: "pickup" | "delivery";
  upfrontAddress?: string;
}

export interface CheckoutData {
  client_name: string;
  client_email: string;
  client_phone: string;
  event_date: string;
  event_time: string;
  guest_count: number;
  fulfillment_type: "pickup" | "delivery";
  delivery_address?: string;
  notes?: string;
  wants_chafing_dishes: boolean;
  wants_service_staff: boolean;
  venue_interest: boolean;
}

export function OrderCheckout({ items, subtotal, onBack, onSubmit, isSubmitting, settings, upfrontFulfillment, upfrontAddress }: OrderCheckoutProps) {
  const showFulfillment = !upfrontFulfillment;
  const pickupEnabled = settings.order_pickup_enabled;
  const deliveryEnabled = settings.order_delivery_enabled;

  const defaultFulfillment = upfrontFulfillment ?? settings.order_default_fulfillment;

  const [form, setForm] = useState<CheckoutData>({
    client_name: "",
    client_email: "",
    client_phone: "",
    event_date: "",
    event_time: "",
    guest_count: settings.order_guest_count_default,
    fulfillment_type: defaultFulfillment,
    delivery_address: upfrontAddress ?? "",
    wants_chafing_dishes: false,
    wants_service_staff: false,
    venue_interest: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (field: keyof CheckoutData, value: CheckoutData[keyof CheckoutData]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const minDate = settings.order_lead_time_days > 0
    ? addDays(startOfDay(new Date()), settings.order_lead_time_days).toISOString().split("T")[0]
    : undefined;

  // Parse blackout dates and available days
  const blackoutDates = useMemo(() => {
    try { return JSON.parse(settings.order_blackout_dates) as string[]; } catch { return []; }
  }, [settings.order_blackout_dates]);

  const availableDays = useMemo(() => {
    try { return JSON.parse(settings.order_available_days) as string[]; } catch { return ["mon","tue","wed","thu","fri","sat","sun"]; }
  }, [settings.order_available_days]);

  const dayAbbrs = ["sun","mon","tue","wed","thu","fri","sat"];

  // Cost preview (#302): null fee means "quote", so there's no estimated total for delivery.
  const deliveryFee = parseDeliveryFee(settings.order_delivery_fee);
  const isDelivery = form.fulfillment_type === "delivery";
  const estimatedTotal = isDelivery ? (deliveryFee === null ? null : subtotal + deliveryFee) : subtotal;

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.client_name.trim()) errs.client_name = "Required";
    if (!form.client_email.trim() || !/\S+@\S+\.\S+/.test(form.client_email)) errs.client_email = "Valid email required";
    if (!form.client_phone.trim()) errs.client_phone = "Required";
    if (!form.event_date) errs.event_date = "Required";
    if (minDate && form.event_date < minDate) errs.event_date = `Must be at least ${settings.order_lead_time_days} days from today`;
    if (form.event_date && blackoutDates.includes(form.event_date)) errs.event_date = "This date is unavailable";
    if (form.event_date) {
      const dayIdx = getDay(parseISO(form.event_date));
      if (!availableDays.includes(dayAbbrs[dayIdx])) errs.event_date = "We don't serve events on this day of the week";
    }
    if (!form.event_time.trim()) errs.event_time = "Required";
    if (form.guest_count < 1) errs.guest_count = "Must be at least 1";
    if (form.fulfillment_type === "delivery" && !form.delivery_address?.trim()) errs.delivery_address = "Required for delivery";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit(form);
  };

  const hasUpsells = settings.order_upsell_chafing_enabled || settings.order_upsell_staff_enabled || settings.order_upsell_venue_enabled;

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-8">
      {/* Back + summary */}
      <div>
        <button type="button" onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to menu
        </button>
        <h2 className="font-heading text-2xl font-bold text-foreground">{settings.order_checkout_heading}</h2>
        <p className="text-muted-foreground mt-1">{items.length} item{items.length !== 1 ? "s" : ""} · ${subtotal.toFixed(2)} subtotal</p>
      </div>

      {/* Order summary */}
      <div className="border border-border rounded-lg p-4 bg-muted/30 space-y-2">
        {items.map((item) => (
          <div key={item.id} className="flex justify-between text-sm">
            <span>{item.quantity}× {item.name} <span className="text-muted-foreground">({item.label || item.pricingType.replace(/_/g, " ")})</span></span>
            <span className="font-medium">${(item.unitPrice * item.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="border-t border-border pt-2 flex justify-between font-semibold">
          <span>Subtotal</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
      </div>

      {/* Upsells */}
      {hasUpsells && (
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-foreground text-lg">{settings.order_upsell_section_title}</h3>
          {settings.order_upsell_chafing_enabled && (
            <label className="flex items-start gap-3 p-4 border border-border rounded-lg hover:border-primary/50 cursor-pointer transition-colors">
              <Checkbox checked={form.wants_chafing_dishes} onCheckedChange={(v) => set("wants_chafing_dishes", !!v)} className="mt-0.5" />
              <div>
                <div className="flex items-center gap-2 font-medium text-foreground">
                  <Flame className="h-4 w-4 text-accent" /> {settings.order_upsell_chafing_title}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{settings.order_upsell_chafing_description}</p>
              </div>
            </label>
          )}
          {settings.order_upsell_staff_enabled && (
            <label className="flex items-start gap-3 p-4 border border-border rounded-lg hover:border-primary/50 cursor-pointer transition-colors">
              <Checkbox checked={form.wants_service_staff} onCheckedChange={(v) => set("wants_service_staff", !!v)} className="mt-0.5" />
              <div>
                <div className="flex items-center gap-2 font-medium text-foreground">
                  <Users className="h-4 w-4 text-accent" /> {settings.order_upsell_staff_title}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{settings.order_upsell_staff_description}</p>
              </div>
            </label>
          )}
          {settings.order_upsell_venue_enabled && (
            <label className="flex items-start gap-3 p-4 border border-border rounded-lg hover:border-primary/50 cursor-pointer transition-colors bg-accent/5">
              <Checkbox checked={form.venue_interest} onCheckedChange={(v) => set("venue_interest", !!v)} className="mt-0.5" />
              <div>
                <div className="flex items-center gap-2 font-medium text-foreground">
                  <MapPin className="h-4 w-4 text-accent" /> {settings.order_upsell_venue_title}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{settings.order_upsell_venue_description}</p>
              </div>
            </label>
          )}
        </div>
      )}

      {/* Event details */}
      <div className="space-y-4">
        <h3 className="font-heading font-semibold text-foreground text-lg">Event Details</h3>

        {showFulfillment && (pickupEnabled || deliveryEnabled) && (
          <>
            <div className="flex gap-3">
              {pickupEnabled && (
                <button
                  type="button"
                  onClick={() => set("fulfillment_type", "pickup")}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border text-sm font-medium transition-colors ${
                    form.fulfillment_type === "pickup"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  <Store className="h-4 w-4" /> {settings.order_pickup_label}
                </button>
              )}
              {deliveryEnabled && (
                <button
                  type="button"
                  onClick={() => set("fulfillment_type", "delivery")}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border text-sm font-medium transition-colors ${
                    form.fulfillment_type === "delivery"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  <Truck className="h-4 w-4" /> {settings.order_delivery_label}
                </button>
              )}
            </div>
            {form.fulfillment_type === "delivery" && settings.order_delivery_zone_note && (
              <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{settings.order_delivery_zone_note}</span>
              </div>
            )}
          </>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="event_date">Event Date *</Label>
            <Input id="event_date" type="date" min={minDate} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className={errors.event_date ? "border-destructive" : ""} />
            {errors.event_date && <p className="text-xs text-destructive mt-1">{errors.event_date}</p>}
          </div>
          <div>
            <Label htmlFor="event_time">Event Time *</Label>
            <Select value={form.event_time} onValueChange={(v) => set("event_time", v)}>
              <SelectTrigger id="event_time" className={errors.event_time ? "border-destructive" : ""}>
                <SelectValue placeholder="Select a time" />
              </SelectTrigger>
              <SelectContent>
                {TIME_SLOTS.map((slot) => (
                  <SelectItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.event_time && <p className="text-xs text-destructive mt-1">{errors.event_time}</p>}
          </div>
        </div>

        <div>
          <Label htmlFor="guest_count">Estimated Guests *</Label>
          <Input id="guest_count" type="number" min={1} value={form.guest_count} onChange={(e) => set("guest_count", parseInt(e.target.value) || 1)} className={errors.guest_count ? "border-destructive" : ""} />
          {errors.guest_count && <p className="text-xs text-destructive mt-1">{errors.guest_count}</p>}
        </div>

        {form.fulfillment_type === "delivery" && !upfrontAddress && (
          <div>
            <Label htmlFor="delivery_address">Delivery Address *</Label>
            <Input id="delivery_address" value={form.delivery_address || ""} onChange={(e) => set("delivery_address", e.target.value)} placeholder="Full address" className={errors.delivery_address ? "border-destructive" : ""} />
            {errors.delivery_address && <p className="text-xs text-destructive mt-1">{errors.delivery_address}</p>}
          </div>
        )}

        <div>
          <Label htmlFor="notes">Special Requests</Label>
          <Textarea id="notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder={settings.order_special_requests_placeholder} rows={3} />
        </div>
      </div>

      {/* Contact info */}
      <div className="space-y-4">
        <h3 className="font-heading font-semibold text-foreground text-lg">Contact Information</h3>
        <div>
          <Label htmlFor="client_name">Name *</Label>
          <Input id="client_name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className={errors.client_name ? "border-destructive" : ""} />
          {errors.client_name && <p className="text-xs text-destructive mt-1">{errors.client_name}</p>}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="client_email">Email *</Label>
            <Input id="client_email" type="email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} className={errors.client_email ? "border-destructive" : ""} />
            {errors.client_email && <p className="text-xs text-destructive mt-1">{errors.client_email}</p>}
          </div>
          <div>
            <Label htmlFor="client_phone">Phone *</Label>
            <Input id="client_phone" type="tel" placeholder="(416) 555-0000" aria-describedby="client_phone_help" value={formatPhoneInput(form.client_phone)} onChange={(e) => set("client_phone", formatPhoneInput(e.target.value))} className={errors.client_phone ? "border-destructive" : ""} />
            <p id="client_phone_help" className="text-xs text-muted-foreground mt-1">We'll call to confirm your order and arrange payment.</p>
            {errors.client_phone && <p className="text-xs text-destructive mt-1">{errors.client_phone}</p>}
          </div>
        </div>
      </div>

      {/* Social proof right before the submit CTA, where hesitation peaks (#256). */}
      <TestimonialStrip limit={2} />

      {/* Submit */}
      <div className="border-t border-border pt-6">
        <div data-testid="order-cost-preview" className="space-y-1 mb-4 text-sm">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div aria-live="polite" className="space-y-1">
            {isDelivery && (
              <div className="flex justify-between">
                <span>Delivery</span>
                <span>{deliveryFee === null ? "Quoted by our team" : `$${deliveryFee.toFixed(2)}`}</span>
              </div>
            )}
            {estimatedTotal !== null && (
              <div className="flex justify-between font-semibold text-base">
                <span>Estimated total</span>
                <span>${estimatedTotal.toFixed(2)}</span>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Taxes and final total confirmed by our team.</p>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{settings.order_disclaimer_text}</p>
        <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : settings.order_submit_button_text}
        </Button>
      </div>
    </form>
  );
}
